import { css, html, nothing } from 'lit';
import { api } from '../../lib/api.js';
import { groupByMilestone, milestoneDateLabel } from '../../lib/available-commissions.js';
import { BaseComponent } from '../../lib/base-component.js';
import { briefHTML } from '../../lib/brief.js';
import { BRIEF_STYLES } from '../../lib/brief-styles.js';
import { loadConfig } from '../../lib/config.js';
import {
    downloadMarkdown,
    formatCents,
    formatMediumDate,
    sizeLabel,
    versionLabel,
} from '../../lib/format.js';
import { router } from '../../lib/router.js';
import { commissionTemplateLinks } from '../../lib/templates.js';
import '../shared/image-lightbox.js';
import '../shared/media-thumb.js';

class AvailableCommissionsView extends BaseComponent {
    static properties = {
        _commissions: { type: Array, state: true },
        _loading: { type: Boolean, state: true },
        _error: { type: String, state: true },
        _view: { type: String, state: true }, // 'list', 'detail'
        _selected: { type: Object, state: true },
        _modal: { type: String, state: true }, // null, 'style-guide', 'technical-spec'
        _lightboxSrc: { type: String, state: true },
        _claiming: { type: Boolean, state: true },
        _config: { type: Object, state: true },
    };

    static styles = [
        BRIEF_STYLES,
        css`
        :host { display: block; }
        .page-header { margin-bottom: 0.5rem; }
        .page-header h4 { margin: 0; font-size: 1.5rem; font-weight: 600; }
        .page-subtitle { color: #666; font-size: 0.9rem; margin-bottom: 1.5rem; }
        table { font-size: 0.9rem; }
        .milestone-section { margin-bottom: 2rem; }
        .milestone-header { border-bottom: 2px solid #dee2e6; padding-bottom: 0.5rem; margin-bottom: 0.5rem; }
        .milestone-header h5 { margin: 0; font-size: 1.1rem; font-weight: 600; display: flex; gap: 0.75rem; align-items: baseline; flex-wrap: wrap; }
        .milestone-meta { font-size: 0.85rem; font-weight: 400; color: #666; }
        .milestone-description { font-size: 0.85rem; color: #555; margin: 0.25rem 0 0; white-space: pre-wrap; }
        .back-link {
            display: inline-flex; align-items: center; gap: 0.25rem;
            color: #666; text-decoration: none; font-size: 0.9rem; margin-bottom: 1rem;
        }
        .back-link:hover { color: #333; }
        .detail-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 1.5rem; }
        .detail-header h4 { margin: 0; font-size: 1.5rem; font-weight: 600; }
        .detail-section { border: 1px solid #eee; border-radius: 8px; padding: 1.25rem; margin-bottom: 1.25rem; }
        .detail-section h6 { font-weight: 600; margin-bottom: 1rem; }
        .brief-text { font-size: 0.9rem; }
        .spec-text { white-space: pre-wrap; font-size: 0.9rem; line-height: 1.6; }
        .detail-layout { display: flex; gap: 2rem; }
        .detail-main { flex: 1; min-width: 0; }
        .detail-sidebar { width: 280px; flex-shrink: 0; }
        .ref-images-grid { display: flex; flex-wrap: wrap; gap: 0.75rem; }
        .ref-thumb {
            width: 120px; border: 2px solid #dee2e6; border-radius: 6px; overflow: hidden; cursor: pointer;
        }
        .ref-thumb img { width: 100%; aspect-ratio: 1; object-fit: contain; background: #f8f9fa; transition: opacity 0.15s; }
        .ref-thumb img:hover { opacity: 0.8; }
        .ref-thumb-info { padding: 0.25rem 0.5rem; font-size: 0.7rem; color: #666; }
        .spec-link {
            display: inline-flex; align-items: center; gap: 0.5rem;
            padding: 0.5rem 0.75rem; border: 1px solid #dee2e6; border-radius: 0.375rem;
            text-decoration: none; color: #333; font-size: 0.9rem;
        }
        .spec-link:hover { background: #f8f9fa; border-color: #adb5bd; }
        .spec-link i { color: #666; }
        .modal-overlay {
            position: fixed; inset: 0; background: rgba(0,0,0,0.5);
            display: flex; align-items: center; justify-content: center; z-index: 9998;
        }
        .modal-panel {
            background: white; border-radius: 8px; width: 90vw; max-width: 700px; max-height: 85vh;
            display: flex; flex-direction: column; box-shadow: 0 1rem 3rem rgba(0,0,0,0.2);
        }
        .modal-header {
            display: flex; align-items: center; justify-content: space-between;
            padding: 1rem 1.25rem; border-bottom: 1px solid #dee2e6;
        }
        .modal-header h5 { margin: 0; font-weight: 600; }
        .modal-body { padding: 1.25rem; overflow-y: auto; flex: 1; }
        .modal-footer {
            display: flex; justify-content: flex-end; gap: 0.5rem;
            padding: 0.75rem 1.25rem; border-top: 1px solid #dee2e6;
        }
    `,
    ];

    constructor() {
        super();
        this._commissions = [];
        this._loading = true;
        this._error = null;
        this._view = 'list';
        this._selected = null;
        this._modal = null;
        this._lightboxSrc = '';
        this._claiming = false;
    }

    async connectedCallback() {
        super.connectedCallback();
        this._unsubscribeRouter = router.subscribe(() => this._syncFromRoute());
        this._syncFromRoute();
        loadConfig()
            .then((cfg) => {
                this._config = cfg;
            })
            .catch(() => {});
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        if (this._unsubscribeRouter) this._unsubscribeRouter();
    }

    async _syncFromRoute() {
        const path = router.getPath();
        if (path.match(/^\/available-commissions\/[^/]+$/) && router.params.id) {
            await this._loadDetail(router.params.id);
            this._view = 'detail';
        } else {
            this._view = 'list';
            this._selected = null;
            this._loadCommissions();
        }
    }

    async _loadCommissions() {
        this._loading = true;
        this._error = null;
        try {
            const data = await api.get('/api/commissions/available');
            this._commissions = data.commissions || [];
        } catch (err) {
            this._error = err.message;
        } finally {
            this._loading = false;
        }
    }

    async _loadDetail(id) {
        this._error = null;
        try {
            this._selected = await api.get(`/api/commissions/available/${id}`);
        } catch (err) {
            this._error = err.message;
        }
    }

    _priorityBadge(priority) {
        const colors = {
            urgent: 'bg-danger',
            high: 'bg-warning text-dark',
            medium: 'bg-secondary',
            low: 'bg-light text-dark',
        };
        return html`<span class="badge ${colors[priority] || 'bg-secondary'}" style="font-size: 0.75rem;">${priority}</span>`;
    }

    async _claimCommission() {
        this._claiming = true;
        this._error = null;
        try {
            await api.post(`/api/commissions/available/${this._selected.id}/claim`);
            router.navigate('/available-commissions');
        } catch (err) {
            this._error = err.message;
        } finally {
            this._claiming = false;
        }
    }

    _renderReferenceImages(images) {
        if (!images || images.length === 0) return nothing;
        return html`
            <div class="detail-sidebar">
                <h6 style="font-weight: 600; font-size: 0.85rem; margin-bottom: 0.5rem;">Reference Images</h6>
                <div class="ref-images-grid">
                    ${images.map(
                        (img) => html`
                        <div class="ref-thumb">
                            <media-thumb src="${img.file_url}" alt="${versionLabel(img.version)}"
                                 @media-click="${() => (this._lightboxSrc = img.file_url)}"></media-thumb>
                            <div class="ref-thumb-info">${versionLabel(img.version)}</div>
                        </div>
                    `,
                    )}
                </div>
            </div>
        `;
    }

    _renderList() {
        return html`
            <div class="page-header">
                <h4>Available Commissions</h4>
            </div>
            <p class="page-subtitle">Open commissions available for freelance artists.</p>

            ${
                this._loading
                    ? html`
                <div class="text-center py-4"><div class="spinner-border spinner-border-sm text-primary" role="status"></div></div>
            `
                    : html`
                ${groupByMilestone(this._commissions).map((section) => this._renderSection(section))}

                ${this._commissions.length === 0 ? html`<p class="text-muted text-center py-3">No commissions available right now.</p>` : nothing}
            `
            }
        `;
    }

    /**
     * One milestone's open commissions (issue #223): a header with its name,
     * target date, description and count, then the table.
     * @param {import('../../lib/available-commissions.js').MilestoneSection} section
     */
    _renderSection(section) {
        const count = section.commissions.length;
        return html`
            <section class="milestone-section">
                <div class="milestone-header">
                    <h5>
                        <span>${section.name}</span>
                        <span class="milestone-meta">
                            ${section.targetDate ? html`<i class="bi bi-calendar-event"></i> ${milestoneDateLabel(section.targetDate)} · ` : nothing}
                            ${count} open commission${count === 1 ? '' : 's'}
                        </span>
                    </h5>
                    ${section.description ? html`<p class="milestone-description">${section.description}</p>` : nothing}
                </div>
                <table class="table table-hover">
                    <thead>
                        <tr>
                            <th>Slug</th>
                            <th>Priority</th>
                            <th>Size</th>
                            <th>Rate</th>
                            <th>Posted</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${section.commissions.map(
                            (c) => html`
                            <tr style="cursor: pointer;" @click="${() => router.navigate(`/available-commissions/${c.id}`)}">
                                <td><span style="color: #0d6efd; font-weight: 500;">${c.slug}</span></td>
                                <td>${this._priorityBadge(c.priority)}</td>
                                <td>${c.miniature_size || '-'}</td>
                                <td>${formatCents(c.rate_cents)}</td>
                                <td class="text-muted">${formatMediumDate(c.created_at)}</td>
                            </tr>
                        `,
                        )}
                    </tbody>
                </table>
            </section>
        `;
    }

    _renderDetail() {
        const c = this._selected;
        if (!c) return nothing;

        return html`
            <a class="back-link" href="/available-commissions">
                <i class="bi bi-arrow-left"></i> Back to Available Commissions
            </a>

            <div class="detail-header">
                <h4>${c.slug}</h4>
                <button class="btn btn-primary" ?disabled="${this._claiming}" @click="${this._claimCommission}">
                    ${this._claiming ? 'Claiming...' : 'Claim Commission'}
                </button>
            </div>

            <div class="detail-layout">
                <div class="detail-main">
                    <div class="detail-section">
                        <div class="row mb-2">
                            <div class="col-6">
                                <span class="text-muted small">Type</span>
                                <div class="fw-medium">${c.asset_type}</div>
                            </div>
                            <div class="col-6">
                                <span class="text-muted small">Rate</span>
                                <div class="fw-medium">${formatCents(c.rate_cents)}</div>
                            </div>
                        </div>
                        ${
                            c.milestone_name
                                ? html`
                            <div class="row mb-2">
                                <div class="col-12">
                                    <span class="text-muted small">Milestone</span>
                                    <div class="fw-medium">
                                        ${c.milestone_name}${c.milestone_target_date ? html` <span class="text-muted small">· due ${milestoneDateLabel(c.milestone_target_date)}</span>` : nothing}
                                    </div>
                                </div>
                            </div>
                        `
                                : nothing
                        }
                        ${
                            c.asset_type === 'miniature' && c.miniature_size
                                ? html`
                            <div class="row">
                                <div class="col-6">
                                    <span class="text-muted small">Size</span>
                                    <div class="fw-medium">${sizeLabel('miniature', c.miniature_size)}</div>
                                </div>
                            </div>
                        `
                                : nothing
                        }
                    </div>

                    <div class="detail-section">
                        <h6>Brief</h6>
                        <div class="brief-text brief-md" .innerHTML="${briefHTML(c.brief)}"></div>
                    </div>

                    <div class="detail-section">
                        <h6>Reference Documents</h6>
                        <div class="d-flex gap-3">
                            ${
                                c.style_guide
                                    ? html`
                                <a class="spec-link" href="#" @click="${(e) => {
                                    e.preventDefault();
                                    this._modal = 'style-guide';
                                }}">
                                    <i class="bi bi-palette"></i> Style Guide
                                </a>
                            `
                                    : nothing
                            }
                            ${
                                c.technical_spec
                                    ? html`
                                <a class="spec-link" href="#" @click="${(e) => {
                                    e.preventDefault();
                                    this._modal = 'technical-spec';
                                }}">
                                    <i class="bi bi-rulers"></i> Technical Spec
                                </a>
                            `
                                    : nothing
                            }
                            ${commissionTemplateLinks(c, this._config).map(
                                (link) => html`
                                    <a class="spec-link" href="${link.url}" download>
                                        <i class="bi bi-file-earmark-arrow-down"></i> ${link.label}
                                    </a>
                                `,
                            )}
                        </div>
                    </div>
                </div>

                ${this._renderReferenceImages(c.reference_images)}
            </div>

            <image-lightbox .src="${this._lightboxSrc}" @close="${() => (this._lightboxSrc = '')}"></image-lightbox>
        `;
    }

    _renderModal() {
        const c = this._selected;
        if (!c || !this._modal) return nothing;

        const isStyleGuide = this._modal === 'style-guide';
        const title = isStyleGuide ? 'Miniature Style Guide' : 'Miniature Technical Specification';
        const content = isStyleGuide ? c.style_guide : c.technical_spec;
        const filename = isStyleGuide ? 'miniature-style-guide.md' : 'miniature-technical-spec.md';

        if (!content) return nothing;

        return html`
            <div class="modal-overlay" @click="${(e) => {
                if (e.target === e.currentTarget) this._modal = null;
            }}">
                <div class="modal-panel">
                    <div class="modal-header">
                        <h5>${title}</h5>
                        <button class="btn-close" @click="${() => (this._modal = null)}"></button>
                    </div>
                    <div class="modal-body">
                        <div class="spec-text">${content}</div>
                    </div>
                    <div class="modal-footer">
                        <button class="btn btn-outline-secondary btn-sm" @click="${() => downloadMarkdown(filename, content)}">
                            <i class="bi bi-download"></i> Download .md
                        </button>
                        <button class="btn btn-secondary btn-sm" @click="${() => (this._modal = null)}">Close</button>
                    </div>
                </div>
            </div>
        `;
    }

    render() {
        return html`
            ${this._error ? html`<div class="alert alert-danger">${this._error}</div>` : ''}
            ${this._view === 'list' ? this._renderList() : ''}
            ${this._view === 'detail' ? this._renderDetail() : ''}
            ${this._renderModal()}
        `;
    }
}

customElements.define('available-commissions-view', AvailableCommissionsView);
